import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieGrid from "@/components/feature/MovieGrid";
import SectionHeading from "@/components/feature/SectionHeading";
import Pagination from "@/components/feature/Pagination";
import { getHotMovies, paginate, searchMovies } from "@/utils/movie";

const PAGE_SIZE = 6;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q") || "";
  const page = Number(searchParams.get("page")) || 1;
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const results = useMemo(() => (query ? searchMovies(query) : []), [query]);
  const { items, total, totalPages, current } = paginate(
    results,
    page,
    PAGE_SIZE
  );
  const hot = useMemo(() => getHotMovies(5), []);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    setSearchParams(q ? { q } : {}, { replace: true });
  };

  const updatePage = (next: number) => {
    if (next < 1 || next > totalPages) return;
    setSearchParams(next === 1 ? { q: query } : { q: query, page: String(next) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const title = query
    ? `“${query}”的搜索结果 - 免费电影在线观看`
    : "搜索电影 - 免费电影在线观看";
  const description = query
    ? `在免费电影在线观看中搜索“${query}”，共找到 ${total} 部相关影片，涵盖高清电影资源，支持在线观看。`
    : "在免费电影在线观看平台搜索您想看的电影，按片名、导演、演员或类型快速查找高清免费影片。";

  return (
    <SiteLayout>
      <Seo
        title={title}
        description={description}
        keywords={`${query},搜索电影,免费电影在线观看,高清电影,在线观看电影`}
      />

      <div className="border-b border-background-200 bg-primary-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
          <h1 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
            搜索电影
          </h1>
          <p className="mt-1 text-sm text-foreground-600">
            输入片名、导演、演员或类型关键词，快速找到想看的电影
          </p>
          <form
            onSubmit={submitSearch}
            className="mt-5 flex w-full max-w-2xl items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2 focus-within:border-primary-400"
          >
            <i className="ri-search-line text-lg text-foreground-500" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="例如：科幻、动作、星海孤航..."
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400 md:text-base"
            />
            <button
              type="submit"
              className="flex items-center gap-1.5 rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line" />
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6">
        {query ? (
          <>
            <p className="text-sm text-foreground-600">
              搜索
              <span className="mx-1 font-semibold text-primary-600">
                “{query}”
              </span>
              共找到
              <span className="mx-1 font-semibold text-primary-600">{total}</span>
              部影片
              {totalPages > 1 && (
                <span className="ml-2 text-foreground-400">
                  第 {current} / {totalPages} 页
                </span>
              )}
            </p>

            {items.length > 0 ? (
              <>
                <div className="mt-6">
                  <MovieGrid movies={items} />
                </div>
                <Pagination
                  current={current}
                  totalPages={totalPages}
                  onChange={updatePage}
                />
              </>
            ) : (
              <div className="mt-6">
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-14 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
                    <i className="ri-search-eye-line" />
                  </span>
                  <p className="mt-4 text-sm text-foreground-700">
                    没有找到与“{query}”相关的电影
                  </p>
                  <p className="mt-1 text-xs text-foreground-500">
                    试试更换关键词，或看看下面这些热门影片
                  </p>
                </div>

                <div className="mt-10">
                  <SectionHeading
                    icon="ri-fire-line"
                    title="热门电影推荐"
                    subtitle="这些影片同样值得一看"
                  />
                  <MovieGrid movies={hot} />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="mt-6">
            <SectionHeading
              icon="ri-fire-line"
              title="大家都在搜"
              subtitle="还没想好看什么？从这些热门影片开始"
            />
            <MovieGrid movies={hot} />
          </div>
        )}
      </div>
    </SiteLayout>
  );
}